/** Normalize upload/API attachment values to a usable string URL/path. */
export const resolveAttachmentUrl = (data) => {
  if (data == null || data === "") return null;
  if (typeof data === "string") {
    const trimmed = data.trim();
    return trimmed || null;
  }
  if (typeof data === "object") {
    const direct =
      data.url ||
      data.Url ||
      data.fileUrl ||
      data.FileUrl ||
      data.blobUrl ||
      data.BlobUrl ||
      data.path ||
      data.Path ||
      data.filePath ||
      data.FilePath ||
      data.fileName ||
      data.FileName ||
      data.file_name ||
      data.blobName ||
      data.BlobName ||
      data.blob_name ||
      data.attachment ||
      data.Attachment ||
      data.attachmentUrl ||
      data.AttachmentUrl ||
      data.attachment_url ||
      data.sasUrl ||
      data.SasUrl ||
      data.location ||
      data.Location ||
      null;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    if (data.data != null && data.data !== data) {
      return resolveAttachmentUrl(data.data);
    }
    if (data.result != null && data.result !== data) {
      return resolveAttachmentUrl(data.result);
    }
    if (data.value != null && data.value !== data) {
      return resolveAttachmentUrl(data.value);
    }
  }
  return null;
};

/** Read attachment URL from any known API/UI field shape. */
export const getInvoiceAttachment = (invoice) => {
  if (!invoice || typeof invoice !== "object") return "";
  const fromResolve = resolveAttachmentUrl(invoice.attachment ?? invoice.Attachment);
  if (fromResolve) return fromResolve;
  const candidates = [
    invoice.attachment_url,
    invoice.attachmentUrl,
    invoice.AttachmentUrl,
    invoice.fileUrl,
    invoice.FileUrl,
    invoice.file_url,
    invoice.filePath,
    invoice.FilePath,
    invoice.file_path,
    invoice.fileName,
    invoice.FileName,
    invoice.file_name,
    invoice.documentUrl,
    invoice.document_url,
    invoice.blobName,
    invoice.BlobName,
    invoice.blob_name,
    invoice.blobUrl,
    invoice.BlobUrl,
    invoice.sasUrl,
    invoice.SasUrl,
  ];
  for (const candidate of candidates) {
    const resolved = resolveAttachmentUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const ATTACHMENT_MEMORY_KEY = (organizationId) =>
  `hms_invoice_attachment_memory_${organizationId || "0"}`;
const GLOBAL_ATTACHMENT_MEMORY_KEY = "hms_invoice_attachment_memory_all";

const readStorageJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStorageJson = (storage, key, value) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode errors
  }
};

/** Merge org-scoped + global memories (localStorage, with sessionStorage fallback). */
export const readInvoiceAttachmentMemory = (organizationId) => {
  const orgKey = ATTACHMENT_MEMORY_KEY(organizationId);
  const fromLocalOrg = readStorageJson(localStorage, orgKey);
  const fromLocalGlobal = readStorageJson(localStorage, GLOBAL_ATTACHMENT_MEMORY_KEY);
  const fromSessionOrg = readStorageJson(sessionStorage, orgKey);
  const fromSessionGlobal = readStorageJson(sessionStorage, GLOBAL_ATTACHMENT_MEMORY_KEY);
  return {
    ...fromSessionGlobal,
    ...fromSessionOrg,
    ...fromLocalGlobal,
    ...fromLocalOrg,
  };
};

const patchInvoiceAttachmentInListCaches = (invoiceId, attachmentUrl) => {
  if (invoiceId == null || invoiceId === "" || !attachmentUrl) return;
  const id = String(invoiceId);
  const patchStorage = (storage) => {
    try {
      const keys = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key && key.startsWith("hms_invoice_list_")) keys.push(key);
      }
      keys.forEach((key) => {
        try {
          const raw = storage.getItem(key);
          if (!raw) return;
          const parsed = JSON.parse(raw);
          if (!parsed?.data || !Array.isArray(parsed.data)) return;
          let changed = false;
          const nextData = parsed.data.map((inv) => {
            if (!inv || String(inv.id) !== id) return inv;
            if (getInvoiceAttachment(inv) === attachmentUrl) return inv;
            changed = true;
            return { ...inv, attachment: attachmentUrl };
          });
          if (changed) {
            storage.setItem(
              key,
              JSON.stringify({ ...parsed, data: nextData, timestamp: Date.now() })
            );
          }
        } catch {
          // Ignore bad cache entries
        }
      });
    } catch {
      // Ignore
    }
  };
  patchStorage(sessionStorage);
  patchStorage(localStorage);
};

export const rememberInvoiceAttachment = (organizationId, invoiceId, attachmentUrl) => {
  if (invoiceId == null || invoiceId === "") return;
  const key = String(invoiceId);
  const orgKey = ATTACHMENT_MEMORY_KEY(organizationId);

  const writeMemory = (storage) => {
    const orgMemory = readStorageJson(storage, orgKey);
    const globalMemory = readStorageJson(storage, GLOBAL_ATTACHMENT_MEMORY_KEY);
    if (attachmentUrl) {
      orgMemory[key] = attachmentUrl;
      globalMemory[key] = attachmentUrl;
    } else {
      delete orgMemory[key];
      delete globalMemory[key];
    }
    writeStorageJson(storage, orgKey, orgMemory);
    writeStorageJson(storage, GLOBAL_ATTACHMENT_MEMORY_KEY, globalMemory);
  };

  // Prefer localStorage so attachments survive tab switches / reloads
  writeMemory(localStorage);
  writeMemory(sessionStorage);

  if (attachmentUrl) {
    patchInvoiceAttachmentInListCaches(invoiceId, attachmentUrl);
  }
};

/** Prefer API attachment when present; otherwise keep previous/local remembered URL. */
export const mergeInvoicesPreservingAttachments = (
  incoming = [],
  previous = [],
  attachmentMemory = {}
) => {
  const prevById = new Map(
    (Array.isArray(previous) ? previous : [])
      .filter((inv) => inv?.id != null)
      .map((inv) => [String(inv.id), inv])
  );

  return (Array.isArray(incoming) ? incoming : []).map((inv) => {
    if (!inv || inv.id == null) return inv;
    const id = String(inv.id);
    const prev = prevById.get(id);
    const incomingAttachment = getInvoiceAttachment(inv);
    const previousAttachment = getInvoiceAttachment(prev);
    const remembered = attachmentMemory?.[id] || "";
    const attachment =
      incomingAttachment || previousAttachment || remembered || "";
    return attachment ? { ...inv, attachment } : { ...inv };
  });
};

/** Human-readable filename for attachment URL/path or plain name. */
export const getInvoiceAttachmentDisplayName = (attachmentValue) => {
  const resolved =
    resolveAttachmentUrl(attachmentValue) ||
    (typeof attachmentValue === "string" ? attachmentValue.trim() : "");
  if (!resolved) return "";
  if (/^https?:\/\//i.test(resolved)) {
    try {
      const pathParts = new URL(resolved).pathname.split("/").filter(Boolean);
      return decodeURIComponent(pathParts[pathParts.length - 1] || "attachment");
    } catch {
      const segment = resolved.split("/").pop() || resolved;
      return decodeURIComponent(segment.split("?")[0].split("#")[0]);
    }
  }
  const segment = resolved.split(/[/\\]/).pop() || resolved;
  return segment.split("?")[0].split("#")[0] || resolved;
};

/**
 * Download an invoice attachment in the browser (Azure blob URL or API path).
 * @param {string|object} attachmentValue
 * @param {{ silent?: boolean }} [options] - silent: skip success toast (e.g. InvoiceTable)
 * @returns {Promise<object|void>}
 */
export const downloadInvoiceAttachment = async (attachmentValue, options = {}) => {
  const { silent = false } = options;
  const { downloadFile } = await import("../services/uploadService");
  const { toast } = await import("react-toastify");

  const url =
    resolveAttachmentUrl(attachmentValue) ||
    (typeof attachmentValue === "string" ? attachmentValue.trim() : "");

  if (!url) {
    toast.error("No attachment available to download");
    return;
  }

  try {
    const result = await downloadFile(url);
    if (!silent) {
      const name =
        result?.fileName || getInvoiceAttachmentDisplayName(url) || "attachment";
      toast.success(`Downloaded: ${name}`);
    }
    return result;
  } catch (error) {
    const message = error?.message || "Failed to download attachment";
    toast.error(message);
    throw error;
  }
};

/** Download a not-yet-uploaded File selected in a form. */
export const downloadLocalAttachmentFile = async (file, options = {}) => {
  const { silent = false } = options;
  if (!(file instanceof File)) return;
  const { triggerBrowserFileDownload } = await import("../services/uploadService");
  const { toast } = await import("react-toastify");
  triggerBrowserFileDownload(file, file.name);
  if (!silent) {
    toast.success(`Downloaded: ${file.name}`);
  }
};

/** Normalize create/update API payloads that wrap the invoice record. */
export const unwrapInvoiceRecord = (payload) => {
  if (payload == null) return payload;
  if (Array.isArray(payload)) {
    return payload.length === 1 ? unwrapInvoiceRecord(payload[0]) : payload;
  }
  if (typeof payload !== "object") return payload;

  if (payload.id != null || payload.invoiceId != null) return payload;

  const nestedKeys = ["data", "invoice", "result", "body", "content", "record"];
  for (const key of nestedKeys) {
    const nested = payload[key];
    if (!nested) continue;
    if (Array.isArray(nested) && nested.length === 1) {
      const unwrapped = unwrapInvoiceRecord(nested[0]);
      if (unwrapped && typeof unwrapped === "object" && (unwrapped.id != null || unwrapped.invoiceId != null)) {
        return unwrapped;
      }
    }
    if (typeof nested === "object" && !Array.isArray(nested)) {
      const unwrapped = unwrapInvoiceRecord(nested);
      if (unwrapped && typeof unwrapped === "object" && (unwrapped.id != null || unwrapped.invoiceId != null)) {
        return unwrapped;
      }
    }
  }

  return payload;
};
